// src/lib/theme/typography.ts
// ============================================================================
// THE DECK COMPASS: TYPOGRAPHY SYSTEM
// Fonts: DM Sans (primary), DM Serif Text (secondary/editorial)
// Status: LOCKED
// ============================================================================

export const typography = {
  // ========================================================================
  // FONT FAMILIES
  // ========================================================================

  fonts: {
    /**
     * Primary Font: DM Sans
     * Clean, modern, highly readable sans-serif.
     * Usage: Body text, UI labels, buttons, headings (except ritual moments)
     * CSS Variable: --font-dm-sans (set in layout.tsx)
     */
    primary: 'var(--font-dm-sans), sans-serif',

    /**
     * Secondary Font: DM Serif Text
     * Editorial, elegant serif for sacred moments.
     * Usage: Editorial sections, ritual markers, special emphasis only
     * CSS Variable: --font-dm-serif (set in layout.tsx)
     */
    secondary: 'var(--font-dm-serif), serif',
  },

  // ========================================================================
  // FONT SIZES (Pixel values - will map to Tailwind)
  // ========================================================================

  sizes: {
    xs: '12px', // Small labels, metadata
    sm: '14px', // Secondary text
    base: '15px', // Body text, standard
    md: '16px', // Standard UI text
    lg: '18px', // Larger body, emphasis
    xl: '20px', // Card titles
    '2xl': '24px', // Section headings
    '3xl': '28px', // Major headings
    '4xl': '32px', // Page titles
  },

  // ========================================================================
  // FONT WEIGHTS (DM Sans & DM Serif Text weights)
  // ========================================================================

  weights: {
    light: 300, // Rare, for very subtle text
    normal: 400, // Default body text
    medium: 500, // Emphasis, labels
    semibold: 600, // Headings, strong emphasis
    bold: 700, // Titles, high emphasis
  },

  // ========================================================================
  // LINE HEIGHTS (For readability and rhythm)
  // ========================================================================

  lineHeights: {
    tight: '1.2', // Headings
    snug: '1.3', // Titles
    normal: '1.4', // Body text (primary reading)
    relaxed: '1.6', // Longer-form content
    loose: '1.8', // Accessibility, low vision
  },

  // ========================================================================
  // LETTER SPACING (For editorial refinement)
  // ========================================================================

  letterSpacing: {
    tight: '-0.02em',
    normal: '0em',
    wide: '0.02em', // Headings for elegance
    wider: '0.04em', // Labels, small caps
  },

  // ========================================================================
  // SEMANTIC TEXT STYLES (Predefined combinations)
  // Use these in components for consistency
  // ========================================================================

  styles: {
    // Page & Section Titles
    pageTitle: {
      size: '32px',
      weight: 700,
      lineHeight: '1.2',
      fontFamily: 'DM Sans',
    },

    // Card Titles
    cardTitle: {
      size: '20px',
      weight: 600,
      lineHeight: '1.3',
      fontFamily: 'DM Sans',
    },

    // Body Text (Standard)
    body: {
      size: '15px',
      weight: 400,
      lineHeight: '1.4',
      fontFamily: 'DM Sans',
    },

    // Secondary/Metadata Text
    metadata: {
      size: '14px',
      weight: 400,
      lineHeight: '1.4',
      fontFamily: 'DM Sans',
      opacity: 0.7, // Reduced emphasis
    },

    // Button Text
    button: {
      size: '15px',
      weight: 600,
      lineHeight: '1.3',
      fontFamily: 'DM Sans',
    },

    // Editorial/Ritual Text (Serif)
    editorial: {
      size: '16px',
      weight: 400,
      lineHeight: '1.6',
      fontFamily: 'DM Serif Text',
    },
  },
} as const;

export type Typography = typeof typography;